/**
 * Schreibt beim jeweils letzten erfolgreichen Online-Aufruf einen schlanken
 * Snapshot von "Meine Mannschaften" und der "Mannschaftsübersicht" nach
 * localStorage. Ausgelesen werden diese Snapshots ausschließlich von der
 * eigenständigen Offline-Seite public/offline-mannschaften.html – die App selbst
 * zeigt offline keine Daten an und liest hier nichts zurück (siehe
 * useMeineMannschaften.js und MannschaftUebersichtPage.js für die
 * Schreibaufrufe). public/offline.html ist nur der Verbindungshinweis für
 * nicht angemeldete Besucher.
 *
 * Die App lädt online die vollständigen Listen (alle Vereine, Wettkämpfe,
 * Mannschaften, Klassen, Klassengruppen, Stände einer Saison). Für den
 * Offline-Speicher wird davon bewusst nur ein Minimum abgelegt:
 *   - pro Datensatz nur die Felder, die offline-mannschaften.html rendert
 *     (keine Adressen, Geburtsdaten, unbeteiligten Personen usw.),
 *   - und nur die Zeilen, die für die Anzeige dieser einen Seite gebraucht
 *     werden (die eigenen Mannschaften bzw. die eine Mannschaft samt ihren
 *     Spielen und – für die Platzierung – ihrer Klasse), nicht die kompletten
 *     Saison-Listen.
 * Wer das Rendering der Offline-Seite erweitert, muss die dort neu benötigten
 * Felder/Zeilen hier in die passende pick*-/slim*-Funktion aufnehmen.
 */

const PREFIX = 'rwk_offline_v1_';
const teamKey = (teamId) => `${PREFIX}team_${teamId}`;
const meineMannschaftenKey = (schuetzenId) => `${PREFIX}meine_mannschaften_${schuetzenId}`;

const mapArray = (value, fn) => (Array.isArray(value) ? value.map(fn).filter(Boolean) : []);

// --- Feld-Whitelists: exakt das, was offline-mannschaften.html rendert -------

function pickVerein(v) {
  if (!v) return null;
  // buildMannschaftLabel: name || ort; Schlüssel vereinsId (Fallback id).
  return { vereinsId: v.vereinsId ?? v.id ?? null, name: v.name ?? null, ort: v.ort ?? null };
}

function pickKlasse(k) {
  if (!k) return null;
  // toEntry / buildKlassenName: klassengruppenId (Verweis) + rangfolge.
  return {
    klassenId: k.klassenId ?? null,
    klassengruppenId: k.klassengruppenId ?? null,
    rangfolge: k.rangfolge ?? null,
  };
}

function pickKlassengruppe(g) {
  if (!g) return null;
  // buildKlassenName + formatWettkampfart + Sortierung ("Meine Mannschaften").
  return {
    klassengruppenId: g.klassengruppenId ?? null,
    sortierung: g.sortierung ?? null,
    name: g.name ?? null,
    geschlecht: g.geschlecht ?? null,
    altersklasse: g.altersklasse ? { name: g.altersklasse.name ?? null } : null,
    waffenart: g.waffenart
      ? { kuerzel: g.waffenart.kuerzel ?? null, beschreibung: g.waffenart.beschreibung ?? null }
      : null,
    anschlagsart: g.anschlagsart
      ? { kuerzel: g.anschlagsart.kuerzel ?? null, beschreibung: g.anschlagsart.beschreibung ?? null }
      : null,
    wettkampfart: g.wettkampfart
      ? {
        waffenartKuerzel: g.wettkampfart.waffenartKuerzel ?? null,
        anschlagsartKuerzel: g.wettkampfart.anschlagsartKuerzel ?? null,
      }
      : null,
  };
}

// Ansprechpartner-/Mitglieder-Kontakt: nur Name, Telefon und E-Mail.
function pickKontakt(s) {
  if (!s) return null;
  return {
    schuetzenId: s.schuetzenId ?? null,
    vorname: s.vorname ?? null,
    nachname: s.nachname ?? null,
    telefon: s.telefon ?? null,
    email: s.email ?? null,
  };
}

function pickStand(s) {
  if (!s) return null;
  // formatStandAddress + buildMapsUrl + Anzeige "name, ort".
  return {
    standId: s.standId ?? null,
    name: s.name ?? null,
    strasse: s.strasse ?? null,
    plz: s.plz ?? null,
    ort: s.ort ?? null,
    latitude: s.latitude ?? null,
    longitude: s.longitude ?? null,
  };
}

function pickMeineTeam(t) {
  return {
    mannschaftsId: t.mannschaftsId ?? null,
    mannschaftsnummer: t.mannschaftsnummer ?? null,
    klassenId: t.klassenId ?? null,
    vereinsId: t.vereinsId ?? null,
    saisonId: t.saisonId ?? null,
  };
}

// "Meine Mannschaften" – buildMeineMannschaftenGroups() in offline-mannschaften.html.
// Behalten wird nur, was die Seite dort anzeigt: eigene Meldungen der aktiven
// Saison und die Vereinsmannschaften, für die der Schütze Ansprechpartner ist –
// samt der davon referenzierten Vereine/Klassen/Klassengruppen.
function slimMeineMannschaften(data, schuetzenId) {
  const { aktiv, mitData, vereine, klassen, klassengruppen, vTeams } = data ?? {};
  const sid = Number(schuetzenId);
  const activeSaisonId = Number(aktiv?.saisonId);
  const inActiveSaison = (t) => !Number.isFinite(activeSaisonId) || Number(t?.saisonId) === activeSaisonId;

  const mitDataSlim = mapArray(mitData, (mm) => {
    const t = mm?.mannschaft;
    return t && inActiveSaison(t) ? { mannschaft: pickMeineTeam(t) } : null;
  });

  const vTeamsSlim = mapArray(vTeams, (t) => {
    if (!t || Number(t.ansprechpartnerId) !== sid || !inActiveSaison(t)) return null;
    if (String(t.status ?? '').toLowerCase() === 'abgemeldet') return null;
    return { ...pickMeineTeam(t), status: t.status ?? null, ansprechpartnerId: t.ansprechpartnerId ?? null };
  });

  const vereinsIds = new Set([
    ...mitDataSlim.map((mm) => Number(mm.mannschaft.vereinsId)),
    ...vTeamsSlim.map((t) => Number(t.vereinsId)),
  ]);
  const klassenIds = new Set([
    ...mitDataSlim.map((mm) => Number(mm.mannschaft.klassenId)),
    ...vTeamsSlim.map((t) => Number(t.klassenId)),
  ]);
  const klassenSlim = mapArray(klassen, (k) => {
    const picked = pickKlasse(k);
    return picked && klassenIds.has(Number(picked.klassenId)) ? picked : null;
  });
  const klassengruppenIds = new Set(klassenSlim.map((k) => Number(k.klassengruppenId)));

  return {
    aktiv: aktiv ? { saisonId: aktiv.saisonId ?? null } : null,
    mitData: mitDataSlim,
    vereine: mapArray(vereine, (v) => {
      const picked = pickVerein(v);
      return picked && vereinsIds.has(Number(picked.vereinsId)) ? picked : null;
    }),
    klassen: klassenSlim,
    klassengruppen: mapArray(klassengruppen, (g) => {
      const picked = pickKlassengruppe(g);
      return picked && klassengruppenIds.has(Number(picked.klassengruppenId)) ? picked : null;
    }),
    vTeams: vTeamsSlim,
  };
}

// "Mannschaftsübersicht" – buildTeamDetail()/teamDetailHtml() in offline-mannschaften.html.
// Von den kompletten Saison-Listen bleibt nur, was diese eine Mannschaft
// anzeigt: ihre Spiele, deren Gegner, und – für die Platzierung – die übrigen
// Mannschaften/Ergebnisse ihrer Klasse.
function slimTeam(data, teamId) {
  const {
    teamData, teamsData, wettkaempfeData, vereineData, ergMData, staendeData,
    mitgliederData, klassenData, klassengruppenData, ansprechpartnerData, gegnerAnsprechpartnerData,
  } = data ?? {};

  const tid = Number(teamId);
  const allTeams = Array.isArray(teamsData) ? teamsData : [];
  const allWks = Array.isArray(wettkaempfeData) ? wettkaempfeData : [];
  const teamById = new Map(allTeams.map((t) => [Number(t?.mannschaftsId), t]));
  const isOwnGame = (wk) => Number(wk?.heimMannschaft) === tid || Number(wk?.auswaertsMannschaft) === tid;

  // Mannschaften der eigenen Klasse – Grundlage der Platzierungsberechnung.
  const ownKlassenId = Number(teamData?.klassenId);
  const classTeamIds = new Set(
    Number.isFinite(ownKlassenId) && ownKlassenId > 0
      ? allTeams.filter((t) => Number(t?.klassenId) === ownKlassenId).map((t) => Number(t?.mannschaftsId))
      : [],
  );

  // Wettkämpfe: eigene Spiele komplett + abgeschlossene Klassenspiele (Platzierung).
  const wksSlim = allWks.filter((wk) => {
    if (isOwnGame(wk)) return true;
    const heim = Number(wk?.heimMannschaft);
    const ausw = Number(wk?.auswaertsMannschaft);
    return classTeamIds.has(heim) && classTeamIds.has(ausw)
      && String(wk?.status ?? '').toLowerCase() === 'abgeschlossen';
  });
  const wkIds = new Set(wksSlim.map((wk) => Number(wk?.wettkampfId)));

  // Gegner/Heim der eigenen Spiele – nur deren Name + Heimstand werden gerendert.
  const ownGameTeamIds = new Set([tid]);
  wksSlim.forEach((wk) => {
    if (!isOwnGame(wk)) return;
    ownGameTeamIds.add(Number(wk?.heimMannschaft));
    ownGameTeamIds.add(Number(wk?.auswaertsMannschaft));
  });

  const teamIds = new Set([...classTeamIds, ...ownGameTeamIds]);
  const teamsSlim = allTeams
    .filter((t) => teamIds.has(Number(t?.mannschaftsId)))
    .map((t) => ({
      mannschaftsId: t.mannschaftsId ?? null,
      klassenId: t.klassenId ?? null,
      vereinsId: t.vereinsId ?? null,
      mannschaftsnummer: t.mannschaftsnummer ?? null,
      ansprechpartnerId: t.ansprechpartnerId ?? null,
      standId: t.standId ?? null,
    }));

  // Vereine/Stände nur für tatsächlich angezeigte Mannschaften, nicht für die
  // ganze Klasse oder Saison.
  const vereinsIds = new Set([...ownGameTeamIds].map((id) => Number(teamById.get(id)?.vereinsId)));
  const standIds = new Set();
  if (Number.isFinite(Number(teamData?.standId))) standIds.add(Number(teamData.standId));
  wksSlim.forEach((wk) => {
    if (isOwnGame(wk)) standIds.add(Number(teamById.get(Number(wk?.heimMannschaft))?.standId));
  });

  const klassenIds = new Set(teamsSlim.map((t) => Number(t.klassenId)));
  if (Number.isFinite(ownKlassenId)) klassenIds.add(ownKlassenId);
  const klassenSlim = mapArray(klassenData, (k) => {
    const picked = pickKlasse(k);
    return picked && klassenIds.has(Number(picked.klassenId)) ? picked : null;
  });
  const klassengruppenIds = new Set(klassenSlim.map((k) => Number(k.klassengruppenId)));

  return {
    // Nur klassenName (Klasse) und Heimstand werden aus dem eigenen Team abgeleitet.
    teamData: teamData ? { klassenId: teamData.klassenId ?? null, standId: teamData.standId ?? null } : null,
    teamsData: teamsSlim,
    wettkaempfeData: wksSlim.map((wk) => ({
      wettkampfId: wk.wettkampfId ?? null,
      heimMannschaft: wk.heimMannschaft ?? null,
      auswaertsMannschaft: wk.auswaertsMannschaft ?? null,
      status: wk.status ?? null,
      datum: wk.datum ?? null,
      nummer: wk.nummer ?? null,
      runde: wk.runde ?? null,
    })),
    vereineData: mapArray(vereineData, (v) => {
      const picked = pickVerein(v);
      return picked && vereinsIds.has(Number(picked.vereinsId)) ? picked : null;
    }),
    ergMData: mapArray(ergMData, (e) => (wkIds.has(Number(e?.wettkampfId))
      ? { wettkampfId: e.wettkampfId ?? null, mannschaftsId: e.mannschaftsId ?? null, punkte: e.punkte ?? null }
      : null)),
    staendeData: mapArray(staendeData, (s) => {
      const picked = pickStand(s);
      return picked && standIds.has(Number(picked.standId)) ? picked : null;
    }),
    mitgliederData: mapArray(mitgliederData, (mm) => (mm
      ? { meldeergebnis: mm.meldeergebnis ?? null, schuetze: pickKontakt(mm.schuetze) }
      : null)),
    klassenData: klassenSlim,
    klassengruppenData: mapArray(klassengruppenData, (g) => {
      const picked = pickKlassengruppe(g);
      return picked && klassengruppenIds.has(Number(picked.klassengruppenId)) ? picked : null;
    }),
    ansprechpartnerData: pickKontakt(ansprechpartnerData),
    gegnerAnsprechpartnerData: mapArray(gegnerAnsprechpartnerData, pickKontakt),
  };
}

function safeSet(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ savedAt: new Date().toISOString(), data }));
  } catch {
    // z. B. QuotaExceededError oder localStorage im privaten Modus deaktiviert –
    // das Offline-Caching ist ein optionales Extra, kein kritischer Pfad.
  }
}

export function saveTeamSnapshot(teamId, data) {
  if (!Number.isFinite(teamId)) return;
  safeSet(teamKey(teamId), slimTeam(data, teamId));
}

export function saveMeineMannschaftenSnapshot(schuetzenId, data) {
  if (!Number.isFinite(schuetzenId)) return;
  safeSet(meineMannschaftenKey(schuetzenId), slimMeineMannschaften(data, schuetzenId));
}

/**
 * Entfernt alle Offline-Snapshots aus localStorage. Wird beim Logout aufgerufen,
 * damit nach dem Abmelden keine personenbezogenen Daten mehr im Browser liegen.
 */
export function clearOfflineSnapshots() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage nicht verfügbar (privater Modus o. Ä.) – nichts zu tun.
  }
}
